import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EditableTagsSection } from '@/features/shared/components/entityDetails/EditableTagsSection';
import {
  defaultTagColor,
  normalizeTagsAndColors,
} from '@/features/shared/components/entityDetails/EditableTagsSection.utils';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getFilterLabel } from '@/shared/copy/filters';
import { getFormLabel } from '@/shared/copy/forms';
import { useTagOptions } from '@/shared/hooks';
import { LinkSelectDialog, UnlinkItemsDialog } from '@/ui/composites/entityLinking';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { TextField } from '@/ui/primitives/TextField';
import type { TagColor } from '@/shared/data/tags';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';
import type { FeatureDto, ReleaseListItemDto } from '@flowqa/shared';

type ProjectOption = { id: string; name: string };

type SuiteFormValues = { name: string; projectId: string };

type SuiteFormHandlers = {
  onNameChange: (value: string) => void;
  onProjectChange?: (value: string) => void;
};

type AddSuiteDialogProps = {
  open: boolean;
  title: string;
  saving?: boolean;
  values: SuiteFormValues;
  errors?: { name?: FieldErrorProps };
  projectOptions: ProjectOption[];
  showProjectSelect: boolean;
  contextText?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  handlers: SuiteFormHandlers;
};

export const AddSuiteDialog = ({
  open,
  title,
  saving = false,
  values,
  errors,
  projectOptions,
  showProjectSelect,
  contextText,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: AddSuiteDialogProps): ReactElement => {
  const Field = TextField;
  return (
    <AddDialog
      open={open}
      title={title}
      saving={saving}
      description={contextText}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      {showProjectSelect ? (
        <SingleSelectFilterField
          required
          label={getFilterLabel('project')}
          value={values.projectId}
          fullWidth
          options={projectOptions.map((project) => ({
            value: project.id,
            label: project.name,
          }))}
          onChange={(nextValue) => {
            handlers.onProjectChange?.(nextValue);
          }}
        />
      ) : null}
    </AddDialog>
  );
};

type EditSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  values: SuiteFormValues;
  projectId?: string | null;
  tags?: string[];
  errors?: { name?: FieldErrorProps };
  errorMessage?: string | null;
  onClose: () => void;
  onSave: (input: { tags: string[]; colors: TagColor[] }) => void;
  handlers: SuiteFormHandlers;
};

export const EditSuiteDialog = ({
  open,
  saving = false,
  values,
  projectId = null,
  tags = [],
  errors,
  errorMessage,
  onClose,
  onSave,
  handlers,
}: EditSuiteDialogProps): ReactElement => {
  const Field = TextField;
  const { tagOptions } = useTagOptions({ projectId, enabled: open });
  const [draftTags, setDraftTags] = useState<string[]>(tags);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>(
    tags.map((_tag, index) => tagOptions[index]?.color ?? defaultTagColor),
  );
  const initialTagColors = useMemo(
    () =>
      tags.map((tag) => {
        const matchingOption = tagOptions.find((option) => option.value === tag);
        return matchingOption?.color ?? defaultTagColor;
      }),
    [tagOptions, tags],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraftTags(tags);
    setDraftTagColors(initialTagColors);
  }, [initialTagColors, open, tags]);

  const normalizedDraftTags = normalizeTagsAndColors(draftTags, draftTagColors);

  return (
    <AddDialog
      open={open}
      title={getDialogMessage('entity.edit.title', { entity: 'suite' })}
      saving={saving}
      disableSave={values.name.trim().length === 0}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={() => {
        onSave(normalizedDraftTags);
      }}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          handlers.onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <EditableTagsSection
        tags={draftTags}
        colors={draftTagColors}
        tagOptions={tagOptions}
        onTagsChange={setDraftTags}
        onColorsChange={setDraftTagColors}
        helperText="Tags will be applied to all associated suite runs, tests and test runs."
      />
    </AddDialog>
  );
};

type DeleteSuiteDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteSuiteDialog = ({
  open,
  loading = false,
  onClose,
  onConfirm,
}: DeleteSuiteDialogProps): ReactElement => {
  return (
    <ConfirmDialog
      open={open}
      title={getDialogMessage('destructive.delete.title', { entity: 'suite' })}
      description={getDialogMessage('destructive.delete.simpleDescription', { entity: 'suite' })}
      confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'suite' })}
      confirmColor="red"
      loading={loading}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

type LinkFeatureToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  featureOptions: FeatureDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onFeatureChange: (value: string) => void;
};

export const LinkFeatureToSuiteDialog = ({
  open,
  saving = false,
  value,
  featureOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onFeatureChange,
}: LinkFeatureToSuiteDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title={getDialogMessage('linking.link.title', { entity: 'feature', owner: 'suite' })}
      saving={saving}
      label="Feature"
      value={value}
      options={featureOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description={getDialogMessage('linking.link.selectedDescription', {
        entity: 'feature',
        owner: 'suite',
      })}
      onClose={onClose}
      onSave={onSave}
      onChange={onFeatureChange}
    />
  );
};

type UnlinkFeaturesFromSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  featureOptions: FeatureDto[];
  selectedFeatureIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleFeature: (featureId: string) => void;
};

export const UnlinkFeaturesFromSuiteDialog = ({
  open,
  saving = false,
  featureOptions,
  selectedFeatureIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleFeature,
}: UnlinkFeaturesFromSuiteDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title={getDialogMessage('linking.unlink.title', { entity: 'feature', owner: 'suite' })}
      saving={saving}
      items={featureOptions}
      selectedIds={selectedFeatureIds}
      itemLabel="Features"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description={getDialogMessage('linking.unlink.selectedDescription', {
        entity: 'feature',
        owner: 'suite',
      })}
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleFeature}
    />
  );
};

type AddFeatureToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  values: { name: string; status: string };
  errors?: { name?: FieldErrorProps };
  statusOptions: Array<{ value: FeatureDto['status']; label: string }>;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  onNameChange: (value: string) => void;
  onStatusChange: (value: string) => void;
};

type LinkReleaseToSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  value: string;
  releaseOptions: ReleaseListItemDto[];
  errorMessage?: string | null;
  fieldError?: string;
  onClose: () => void;
  onSave: () => void;
  onReleaseChange: (value: string) => void;
};

export const LinkReleaseToSuiteDialog = ({
  open,
  saving = false,
  value,
  releaseOptions,
  errorMessage,
  fieldError,
  onClose,
  onSave,
  onReleaseChange,
}: LinkReleaseToSuiteDialogProps): ReactElement => {
  return (
    <LinkSelectDialog
      open={open}
      title={getDialogMessage('linking.link.title', { entity: 'release', owner: 'suite' })}
      saving={saving}
      label="Release"
      value={value}
      options={releaseOptions}
      fieldError={fieldError}
      errorMessage={errorMessage}
      description={getDialogMessage('linking.link.selectedDescription', {
        entity: 'release',
        owner: 'suite',
      })}
      onClose={onClose}
      onSave={onSave}
      onChange={onReleaseChange}
    />
  );
};

type UnlinkReleasesFromSuiteDialogProps = {
  open: boolean;
  saving?: boolean;
  releaseOptions: ReleaseListItemDto[];
  selectedReleaseIds: string[];
  errorMessage?: string | null;
  disableSave?: boolean;
  onClose: () => void;
  onSave: () => void;
  onToggleRelease: (releaseId: string) => void;
};

export const UnlinkReleasesFromSuiteDialog = ({
  open,
  saving = false,
  releaseOptions,
  selectedReleaseIds,
  errorMessage,
  disableSave = false,
  onClose,
  onSave,
  onToggleRelease,
}: UnlinkReleasesFromSuiteDialogProps): ReactElement => {
  return (
    <UnlinkItemsDialog
      open={open}
      title={getDialogMessage('linking.unlink.title', { entity: 'release', owner: 'suite' })}
      saving={saving}
      items={releaseOptions}
      selectedIds={selectedReleaseIds}
      itemLabel="Releases"
      errorMessage={errorMessage}
      disableSave={disableSave}
      description="Remove direct release links for this suite."
      onClose={onClose}
      onSave={onSave}
      onToggle={onToggleRelease}
    />
  );
};

export const AddFeatureToSuiteDialog = ({
  open,
  saving = false,
  values,
  errors,
  statusOptions,
  errorMessage,
  onClose,
  onSave,
  onNameChange,
  onStatusChange,
}: AddFeatureToSuiteDialogProps): ReactElement => {
  const Field = TextField;
  return (
    <AddDialog
      open={open}
      title={getDialogMessage('entity.addTo.title', { entity: 'feature', owner: 'suite' })}
      saving={saving}
      errorMessage={errorMessage}
      onClose={onClose}
      onSave={onSave}
    >
      <Field
        label={getFormLabel('name')}
        value={values.name}
        onChange={(event) => {
          onNameChange(event.target.value);
        }}
        fieldStyle="settings"
        required
        autoFocus
        {...(errors?.name ?? {})}
      />
      <SingleSelectFilterField
        label={getFormLabel('status')}
        value={values.status}
        fullWidth
        options={statusOptions.map((status) => ({
          value: status.value,
          label: status.label,
        }))}
        onChange={(nextValue) => {
          onStatusChange(nextValue as FeatureDto['status']);
        }}
      />
    </AddDialog>
  );
};
